class BulkAddMobile extends HTMLElement {
  constructor() {
    super();
    this.mutationObserver = null;
    // Cache frequently accessed DOM elements
    this.cachedElements = {
      body: null,
      container: null,
      background: null
    };
  }

  connectedCallback() {
    this.setupMobileQuickAdd();
  }

  disconnectedCallback() {
    // Clean up MutationObserver to prevent memory leaks
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    // Clear cached elements
    this.cachedElements = {
      body: null,
      container: null,
      background: null
    };
  }

  setupMobileQuickAdd() {
    const cardMediaElement = this.closest('.card--media');
    const quickAddButton = cardMediaElement?.querySelector('.product-quick-add__button');

    // Cache body element
    if (!this.cachedElements.body) {
      this.cachedElements.body = document.body;
    }

    // Get or create global containers and cache them
    if (!this.cachedElements.container) {
      this.cachedElements.container = document.querySelector('#global-mobile-quick-add-container');
      if (!this.cachedElements.container) {
        this.cachedElements.container = document.createElement('div');
        this.cachedElements.container.id = 'global-mobile-quick-add-container';
        this.cachedElements.container.className = 'mobile-quick-add-container';
        this.cachedElements.body.appendChild(this.cachedElements.container);
      }
    }

    if (!this.cachedElements.background) {
      this.cachedElements.background = document.querySelector('#global-mobile-quick-add-bg');
      if (!this.cachedElements.background) {
        this.cachedElements.background = document.createElement('div');
        this.cachedElements.background.id = 'global-mobile-quick-add-bg';
        this.cachedElements.background.className = 'mobile-quick-add-bg';
        this.cachedElements.body.appendChild(this.cachedElements.background);
      }
    }

    if (!quickAddButton) {
      return;
    }

    // Click on the quick add button to show the mobile popup
    quickAddButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.showMobileQuickAdd();
    });

    // Click on background to close
    this.cachedElements.background.addEventListener('click', () => {
      this.hideMobileQuickAdd();
    });
  }

  showMobileQuickAdd() {
    // Find the desktop quick-add-bulk element to clone
    const desktopQuickAddBulk = this.closest('.card-wrapper').querySelector('quick-add-bulk');

    if (!desktopQuickAddBulk) {
      console.error('Desktop quick-add-bulk element not found');
      return;
    }

    // Use cached container instead of querySelectorAll
    // Since we're using a single global container, just ensure it's reset
    if (this.cachedElements.container.classList.contains('show')) {
      this.cachedElements.container.classList.remove('show');
    }
    if (this.cachedElements.background.style.display === 'block') {
      this.cachedElements.background.style.display = 'none';
    }

    // Clone the quantity input from desktop version
    const quantityInput = desktopQuickAddBulk.querySelector('quantity-input');
    if (quantityInput) {
      this.cachedElements.container.innerHTML = `
        <div class="mobile-quick-add-body">
          ${quantityInput.outerHTML}
        </div>
      `;

      const mobileInput = this.cachedElements.container.querySelector('.quantity__input');

      // Remove id and name to prevent conflicts with desktop input
      if (mobileInput) {
        mobileInput.removeAttribute('id');
        mobileInput.removeAttribute('name');
      }

      // Re-initialize the quantity input functionality
      this.initializeQuantityInput(this.cachedElements.container, desktopQuickAddBulk);
    }

    // Show the popup
    this.cachedElements.container.classList.add('show');
    this.cachedElements.background.style.display = 'block';
    this.cachedElements.body.classList.add('overflow-hidden');

    // Force display for Android Chrome
    this.cachedElements.container.style.display = 'block';
    this.cachedElements.container.style.bottom = '0';
  }

  hideMobileQuickAdd() {
    this.cachedElements.container.classList.remove('show');
    this.cachedElements.background.style.display = 'none';
    this.cachedElements.body.classList.remove('overflow-hidden');
  }

  showSuccessFeedback(cartCount) {
    // Show success message with cart count
    const successMessage = document.createElement('div');
    successMessage.className = 'cart-update-success';
    successMessage.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 20px; height: 20px; display: inline-block; vertical-align: middle; margin-right: 8px;">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
      <span aria-hidden="true">${cartCount}</span>
    `;
    successMessage.style.cssText = 'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background: rgb(var(--color-button)); color: rgb(var(--color-button-text)); padding: 12px 24px; border-radius: 4px; z-index: 10000; display: flex; align-items: center; font-weight: 600;';
    document.body.appendChild(successMessage);
    setTimeout(() => successMessage.remove(), 2000);
  }

  updateCartBubble() {
    // Fetch cart and update the cart count bubble
    fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(cart => {
        // Get all cart icon containers
        const cartIcons = document.querySelectorAll('#cart-icon-bubble');

        cartIcons.forEach(cartIcon => {
          let bubble = cartIcon.querySelector('.cart-count-bubble');

          if (cart.item_count > 0) {
            // Create bubble if it doesn't exist
            if (!bubble) {
              bubble = document.createElement('div');
              bubble.className = 'cart-count-bubble';
              cartIcon.appendChild(bubble);
            }

            bubble.style.display = '';

            // Update the aria-hidden span
            let ariaSpan = bubble.querySelector('span[aria-hidden="true"]');
            if (!ariaSpan) {
              ariaSpan = document.createElement('span');
              ariaSpan.setAttribute('aria-hidden', 'true');
              bubble.appendChild(ariaSpan);
            }
            ariaSpan.textContent = cart.item_count;

            // Update the visually-hidden span
            let visuallyHiddenSpan = bubble.querySelector('span.visually-hidden');
            if (!visuallyHiddenSpan) {
              visuallyHiddenSpan = document.createElement('span');
              visuallyHiddenSpan.className = 'visually-hidden';
              bubble.appendChild(visuallyHiddenSpan);
            }
            visuallyHiddenSpan.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
          } else {
            // Remove bubble if cart is empty
            if (bubble) {
              bubble.remove();
            }
          }
        });

      })
      .catch(err => {
        console.error('Failed to update cart bubble:', err);
        // Show user feedback for failed cart update
        const errorMessage = document.createElement('div');
        errorMessage.className = 'cart-update-error';
        errorMessage.textContent = 'Failed to update cart. Please refresh the page.';
        errorMessage.style.cssText = 'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background: #dc3545; color: white; padding: 12px 24px; border-radius: 4px; z-index: 10000;';
        document.body.appendChild(errorMessage);
        setTimeout(() => errorMessage.remove(), 3000);
      });
  }

  initializeQuantityInput(container, desktopQuickAddBulk) {
    const input = container.querySelector('.quantity__input');
    const minusButton = container.querySelector('.quantity__button[name="minus"]');
    const plusButton = container.querySelector('.quantity__button[name="plus"]');
    const updateButton = container.querySelector('.quantity__update-button');
    const spinner = container.querySelector('.loading__spinner');

    if (!input) return;

    const originalValue = input.value;
    const desktopInput = desktopQuickAddBulk.querySelector('.quantity__input');

    // Update button visibility on input - only show if value changed
    input.addEventListener('input', () => {
      if (updateButton) {
        if (input.value !== originalValue) {
          updateButton.classList.remove('hidden');
        } else {
          updateButton.classList.add('hidden');
        }
      }
    });

    // Plus button - trigger desktop plus button
    plusButton?.addEventListener('click', () => {
      // Show mobile spinner
      const currentSpinner = this.cachedElements.container.querySelector('.loading__spinner');
      if (currentSpinner) {
        currentSpinner.classList.remove('hidden');
      }

      // Get fresh desktop element and button reference
      const freshDesktopElement = this.closest('.card-wrapper').querySelector('quick-add-bulk');
      const freshDesktopPlusButton = freshDesktopElement?.querySelector('.quantity__button[name="plus"]');

      // Trigger desktop plus button
      if (freshDesktopPlusButton) {
        freshDesktopPlusButton.click();
      }
    });

    // Minus button - trigger desktop minus button
    minusButton?.addEventListener('click', () => {
      // Show mobile spinner
      const currentSpinner = this.cachedElements.container.querySelector('.loading__spinner');
      if (currentSpinner) {
        currentSpinner.classList.remove('hidden');
      }

      // Get fresh desktop element and button reference
      const freshDesktopElement = this.closest('.card-wrapper').querySelector('quick-add-bulk');
      const freshDesktopMinusButton = freshDesktopElement?.querySelector('.quantity__button[name="minus"]');

      // Trigger desktop minus button
      if (freshDesktopMinusButton) {
        freshDesktopMinusButton.click();
      }
    });

    // Track the last typed value
    let lastTypedValue = input.value;
    input.addEventListener('input', () => {
      lastTypedValue = input.value;
    });

    // Update button click
    updateButton?.addEventListener('click', () => {
      // Use the last typed value instead of input.value (which may have been reset)
      const valueToUse = lastTypedValue;

      // Only update if value has changed
      if (valueToUse === originalValue) {
        updateButton.classList.add('hidden');
        return;
      }

      updateButton.classList.add('hidden');
      if (spinner) {
        spinner.classList.remove('hidden');
      }

      // Get fresh desktop element and input reference
      const freshDesktopElement = this.closest('.card-wrapper').querySelector('quick-add-bulk');
      const freshDesktopInput = freshDesktopElement?.querySelector('.quantity__input');

      // Sync input value to desktop and trigger update via desktop update button
      if (freshDesktopInput) {
        freshDesktopInput.value = valueToUse;

        // Trigger the desktop update button instead of change event
        const desktopUpdateButton = freshDesktopElement?.querySelector('.quantity__update-button');
        if (desktopUpdateButton && !desktopUpdateButton.classList.contains('hidden')) {
          desktopUpdateButton.click();
        } else {
          freshDesktopInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Listen for desktop quick-add-bulk DOM changes (innerHTML replacement)
    // Clean up any existing observer first
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Function to set up the observer
    const setupObserver = () => {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }

      // Get fresh reference to desktop element
      const freshDesktopElement = this.closest('.card-wrapper').querySelector('quick-add-bulk');

      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Check if childList changed (innerHTML replacement)
          if (mutation.type === 'childList') {
            // Wait for desktop to settle, then check spinner state
            requestAnimationFrame(() => {
              // Get fresh desktop element reference each time
              const currentDesktopElement = this.closest('.card-wrapper').querySelector('quick-add-bulk');
              const desktopSpinner = currentDesktopElement?.querySelector('.loading__spinner');

              // After innerHTML replacement, new spinner is created and is hidden by default
              if (desktopSpinner && desktopSpinner.classList.contains('hidden')) {
                // Desktop update complete, hide mobile spinner
                const currentSpinner = this.cachedElements.container.querySelector('.loading__spinner');

                if (currentSpinner && !currentSpinner.classList.contains('hidden')) {
                  currentSpinner.classList.add('hidden');
                }

                // Sync mobile input value with desktop
                const mobileInput = this.cachedElements.container.querySelector('.quantity__input');
                const desktopInputValue = currentDesktopElement?.querySelector('.quantity__input')?.value;
                if (mobileInput && desktopInputValue) {
                  mobileInput.value = desktopInputValue;
                }

                // Force update cart count bubble
                this.updateCartBubble();

                // Re-attach observer for next update since innerHTML was replaced
                setupObserver();

                // Keep the mobile quick add container open - don't auto close
              }
            });
          }
        });
      });

      // Observe the entire desktop quick-add-bulk for content changes
      if (freshDesktopElement) {
        this.mutationObserver.observe(freshDesktopElement, {
          childList: true,
          subtree: true
        });
      }
    };

    // Initial observer setup
    setupObserver();
  }
}

if (!customElements.get('quick-add-bulk-mobile')) {
  customElements.define('quick-add-bulk-mobile', BulkAddMobile);
}
